import type { AppDispatch, RootState } from '@/app/store/store';
import { createApiSlice } from '@/shared/features/query/config/query.build';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import type { RtkQueryAdapter } from '@/shared/features/query/infra/adapters/rtk.adapter';
import { executeQuery } from '@/shared/features/query/utils/queryUtils';
import type { PhysicalStoreRepository } from '../../domain/interfaces/PhysicalStoreRepository';
import type { PhysicalStoresDetails } from '../../domain/models/PhysicalStoresDetails';
import type { StoreLocationWidgetData } from '../../domain/models/StoreLocationWidgetData';

export type PhysicalStoresDetailsResponse = PhysicalStoresDetails;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseStoreLocationWidgetResponse(response: unknown): StoreLocationWidgetData | null {
  if (!isObject(response)) return null;
  const data = (response as Record<string, unknown>).data;
  if (!isObject(data)) return null;
  const storeImageUrl = typeof data['store-image'] === 'string' ? data['store-image'] : '';
  const raw = data.nearestPhysicalStoreResponse;
  if (!isObject(raw)) return null;
  const nearest = raw as Record<string, unknown>;
  const nearestStore = {
    storeMessage: typeof nearest.storeMessage === 'string' ? nearest.storeMessage : '',
    description: typeof nearest.description === 'string' ? nearest.description : '',
    destinationPlaceId: typeof nearest.destinationPlaceId === 'string' ? nearest.destinationPlaceId : '',
    latitude: typeof nearest.latitude === 'string' ? nearest.latitude : '',
    longitude: typeof nearest.longitude === 'string' ? nearest.longitude : '',
  };
  if (!nearestStore.storeMessage) return null;
  return { storeImageUrl, nearestStore };
}

const physicalStoreApiSlice = createApiSlice({
  clientId: 'physicalStoreApi',
  tagTypes: ['PhysicalStore'],
  endpoints: {
    getPhysicalStoresDetails: {
      url: '/physical-store/stores-details',
      method: 'GET',
      withCredentials: true,
      transformResponse: (response: unknown): PhysicalStoresDetailsResponse => {
        if (!isObject(response)) return {};
        const cityToStoresMap = (response as Record<string, unknown>).cityToStoresMap;
        return { cityToStoresMap };
      },
      providesTags: ['PhysicalStore'],
    },
    getStoreLocationWidget: {
      url: '/physical-store/store-location-widget',
      method: 'GET',
      withCredentials: true,
      transformResponse: (response: unknown): StoreLocationWidgetData | null =>
        parseStoreLocationWidgetResponse(response),
      providesTags: ['PhysicalStore'],
    },
  },
});

export const physicalStoreApi = physicalStoreApiSlice.queryClient;
export const physicalStoreApiRedux = physicalStoreApiSlice.queryClient as ReduxQueryClient;

/** RTK Query API instance for cache invalidation. */
export const physicalStoreRtkApi = (physicalStoreApiSlice.queryClient as RtkQueryAdapter).getApi();

export class PhysicalStoreAdapter implements PhysicalStoreRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  prefetchStoresDetails(): void {
    const endpoint = physicalStoreApi.endpoints?.getPhysicalStoresDetails;
    if (!endpoint || typeof endpoint.initiate !== 'function') return;
    // RTK Query will dedupe/cached requests; dispatching is enough to warm the cache.
    this.dispatch(endpoint.initiate(undefined) as unknown as Parameters<AppDispatch>[0]);
  }

  selectStoresDetails(state: RootState): PhysicalStoresDetails | undefined {
    // `physicalStoreApi` is exposed via a generic QueryClient interface; cast to access RTK selectors safely.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rtkApi = physicalStoreApi as any;
    const selector = rtkApi?.endpoints?.getPhysicalStoresDetails?.select?.(undefined);
    const queryState = typeof selector === 'function' ? selector(state) : undefined;
    return queryState?.data as PhysicalStoresDetails | undefined;
  }

  async fetchStoreLocationWidget(): Promise<StoreLocationWidgetData | null> {
    try {
      const endpoint = physicalStoreApi.endpoints?.getStoreLocationWidget;
      if (!endpoint || typeof endpoint.initiate !== 'function') return null;
      const initiateResult = endpoint.initiate(undefined);
      const result = await executeQuery<StoreLocationWidgetData | null>(initiateResult, this.dispatch);
      return result ?? null;
    } catch (error) {
      console.error('[PhysicalStoreAdapter] Failed to fetch store location widget:', error);
      return null;
    }
  }

  invalidateStoreLocationWidget(): void {
    try {
      const rtkApi = physicalStoreRtkApi as { util?: { invalidateTags?: (tags: string[]) => unknown } };
      if (rtkApi?.util?.invalidateTags) {
        this.dispatch(rtkApi.util.invalidateTags(['PhysicalStore']) as Parameters<AppDispatch>[0]);
      }
    } catch {
      // ignore
    }
  }
}

